import { useState } from 'react';
import { toast } from 'sonner';
import { ExternalLink, History, Link2, RotateCcw, Trash2 } from 'lucide-react';

import {
  useCMSHistoryQuery,
  useCMSSlugHistoryQuery,
} from '@/client/api/cms/cms.query';
import {
  useDeleteCMSSlugHistoryMutation,
  useRestoreCMSRevisionMutation,
} from '@/client/api/cms/cms.mutation';
import { publicContentUrl } from '@/client/types/cms';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';

type Tab = 'revisions' | 'slugs';

interface CMSPageHistoryPanelProps {
  cmsoid: number;
  currentSlug?: string | null;
  /** When true, restoring a revision navigates via parent callback after success. */
  onRestored?: () => void;
  className?: string;
}

const CMSPageHistoryPanel = ({
  cmsoid,
  currentSlug,
  onRestored,
  className,
}: CMSPageHistoryPanelProps) => {
  const [tab, setTab] = useState<Tab>('revisions');
  const historyQuery = useCMSHistoryQuery(cmsoid);
  const slugQuery = useCMSSlugHistoryQuery(cmsoid);
  const restoreMutation = useRestoreCMSRevisionMutation();
  const deleteSlugMutation = useDeleteCMSSlugHistoryMutation();

  const revisions = historyQuery.data ?? [];
  const slugRows = slugQuery.data ?? [];

  const handleRestore = async (cmshid: number) => {
    if (!window.confirm('Restore this revision? Current state will be snapshotted first.')) return;
    try {
      await restoreMutation.mutateAsync({ cmsoid, cmshid });
      toast.success('Revision restored');
      onRestored?.();
    } catch (err: any) {
      toast.error(err?.message ?? 'Failed to restore revision');
    }
  };

  const handleReleaseSlug = async (cshid: number, slug: string) => {
    if (
      !window.confirm(
        `Release redirect for “${slug}”? Old URLs with this slug will stop resolving to this page.`,
      )
    ) {
      return;
    }
    try {
      await deleteSlugMutation.mutateAsync({ cmsoid, cshid });
      toast.success('Slug redirect released');
    } catch (err: any) {
      toast.error(err?.message ?? 'Failed to release slug');
    }
  };

  return (
    <div className={className}>
      <div className="mb-3 flex gap-1 rounded-md border border-gray-200 bg-gray-50 p-0.5">
        <button
          type="button"
          onClick={() => setTab('revisions')}
          className={`flex flex-1 items-center justify-center gap-1.5 rounded px-2 py-1.5 text-xs font-medium ${
            tab === 'revisions'
              ? 'bg-white text-gray-900 shadow-sm'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <History className="h-3.5 w-3.5" />
          Revisions
          {revisions.length > 0 && (
            <span className="text-gray-400">({revisions.length})</span>
          )}
        </button>
        <button
          type="button"
          onClick={() => setTab('slugs')}
          className={`flex flex-1 items-center justify-center gap-1.5 rounded px-2 py-1.5 text-xs font-medium ${
            tab === 'slugs'
              ? 'bg-white text-gray-900 shadow-sm'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <Link2 className="h-3.5 w-3.5" />
          Slug redirects
          {slugRows.length > 0 && (
            <span className="text-gray-400">({slugRows.length})</span>
          )}
        </button>
      </div>

      {tab === 'revisions' && (
        <div className="space-y-2">
          <p className="text-xs text-gray-500">
            Snapshots taken before each save. Restore rewrites content fields; publish status is left alone.
          </p>
          {historyQuery.isLoading && (
            <div className="space-y-2">
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
            </div>
          )}
          {!historyQuery.isLoading && revisions.length === 0 && (
            <p className="py-4 text-center text-xs text-gray-500">No revisions yet.</p>
          )}
          <ul className="max-h-80 space-y-2 overflow-y-auto">
            {revisions.map((r) => (
              <li
                key={r.cmshid}
                className="rounded-md border border-gray-200 bg-white px-3 py-2"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-gray-900">
                      {r.title || 'Untitled'}
                    </p>
                    <p className="mt-0.5 font-mono text-[11px] text-gray-500">
                      {r.slug || '—'}
                      {r.slug && r.slug !== currentSlug && (
                        <span className="ml-1 text-amber-600">(was)</span>
                      )}
                    </p>
                    <p className="mt-1 text-[11px] text-gray-400">
                      #{r.cmshid}
                      {r.date_added
                        ? ` · ${new Date(String(r.date_added)).toLocaleString()}`
                        : ''}
                    </p>
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="h-7 shrink-0 gap-1"
                    disabled={restoreMutation.isPending}
                    onClick={() => handleRestore(r.cmshid)}
                  >
                    <RotateCcw className="h-3 w-3" />
                    Restore
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {tab === 'slugs' && (
        <div className="space-y-2">
          <p className="text-xs text-gray-500">
            Former slugs still redirect to this page. Release one only when you want that URL free for reuse.
          </p>
          {currentSlug && (
            <div className="rounded-md border border-emerald-100 bg-emerald-50 px-3 py-2 text-xs">
              <span className="font-medium text-emerald-800">Current: </span>
              <span className="font-mono text-emerald-700">{currentSlug}</span>
              <a
                href={publicContentUrl(currentSlug)}
                target="_blank"
                rel="noreferrer"
                className="ml-2 inline-flex items-center gap-0.5 text-emerald-700 hover:underline"
              >
                Open <ExternalLink className="h-3 w-3" />
              </a>
            </div>
          )}
          {slugQuery.isLoading && (
            <div className="space-y-2">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          )}
          {!slugQuery.isLoading && slugRows.length === 0 && (
            <p className="py-4 text-center text-xs text-gray-500">
              No former slugs recorded. Rename the page slug to start a redirect chain.
            </p>
          )}
          <ul className="max-h-80 space-y-2 overflow-y-auto">
            {slugRows.map((row) => (
              <li
                key={row.cshid}
                className="flex items-center justify-between gap-2 rounded-md border border-gray-200 bg-white px-3 py-2"
              >
                <div className="min-w-0">
                  <p className="truncate font-mono text-sm text-gray-900">{row.slug}</p>
                  <p className="mt-0.5 text-[11px] text-gray-400">
                    {row.country_iso2 ? `${row.country_iso2} · ` : 'global · '}
                    {row.date_added
                      ? new Date(String(row.date_added)).toLocaleString()
                      : '—'}
                  </p>
                </div>
                <div className="flex shrink-0 items-center gap-1">
                  <a
                    href={publicContentUrl(row.slug)}
                    target="_blank"
                    rel="noreferrer"
                    className="rounded p-1.5 text-gray-500 hover:bg-gray-100 hover:text-blue-600"
                    title="Open public URL"
                  >
                    <ExternalLink className="h-3.5 w-3.5" />
                  </a>
                  <button
                    type="button"
                    className="rounded p-1.5 text-red-600 hover:bg-red-50"
                    title="Release redirect"
                    disabled={deleteSlugMutation.isPending}
                    onClick={() => handleReleaseSlug(row.cshid, row.slug)}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default CMSPageHistoryPanel;
